// @flow
import React, { useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';
import { useEstimateMLDForFieldGuidance } from '@src/custom-hooks/useEstimateMLDForFieldGuidance';
import { useIsFedRAMPEstimate } from '@src/custom-hooks/useIsFedRAMPEstimate';

import {
  FieldArrayGridConfigItemContext,
  FieldArrayGridSelectField,
} from '../../../formik-utils/FieldArrayGrid';
import {
  GATEWAY_ADD_ON_MLD_METRIC_NAMES,
  IS_GATEWAY_ADD_ON_LIVE_IN_CE1,
  IS_NATIVE_INFERENCE_CCU_LIVE_IN_CE1,
  IS_RTCE_LIVE_IN_CE1,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
  NATIVE_INFERENCE_CCU_MLD_METRIC_NAME,
  RATE_CARD_BACKEND_NAME,
} from '../../../constants';
import { METRICS_LEVEL_DISCOUNTS_TOP_LEVEL_CONFIG_ARRAY_CONFIG_MAP } from '../../../configuration/custom-discounts';
import { DataFetcherContainer } from '../../presentational/DataFetcherContainer';
import { getMetricNameOptions } from '../utils';
import { useGetCustomizableMetricsListQuery } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { getMetricsByAddingDisabledForFieldBasedOnGuidanceAndUsage } from '../mld-for-field/utils';

// RTCE MLD discount metrics (backend identifiers). Hidden from the picker until RTCE is live.
const RTCE_MLD_METRIC_NAMES = ['RTCENumCCUs', 'RTCEStorage'];

export const MetricNameDropdownContainer = () => {
  const { estimateId } = useParams();
  const estimate = useContext(EstimateContext);
  const isUserAdmin = useIsUserAdmin();
  const { allRows, rowValues } = useContext(FieldArrayGridConfigItemContext);
  const mldGuidance = useEstimateMLDForFieldGuidance();
  const isFedRAMPEstimate = useIsFedRAMPEstimate();

  const metricNameConfig = METRICS_LEVEL_DISCOUNTS_TOP_LEVEL_CONFIG_ARRAY_CONFIG_MAP.get(
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME
  );

  const rateCardVersion = estimate.inputs[RATE_CARD_BACKEND_NAME];

  return (
    <DataFetcherContainer
      dataFetchingArgs={useMemo(
        () => [
          {
            rateCardVersion,
          },
          {
            skip: !estimateId,
          },
        ],
        [estimateId, rateCardVersion]
      )}
      dataFetchingFunction={useCallback(useGetCustomizableMetricsListQuery, [])}
    >
      {(metricsList) => {
        // todo::Check with BE team if this should go into BE..
        let metricsListToUse = metricsList.metrics.filter((x) => x.is_customizable === true);

        // We need to remove these metrics as currently, in CE, users are not allowed to use separate discounts for UnusedCommittedCapacity
        metricsListToUse = metricsListToUse.filter(
          (x) => !['UnusedCommittedCapacity-CFU', 'UnusedCommittedCapacity-ECKU'].includes(x.name)
        );

        // RTCE metrics are backend-driven; keep them out of the MLD picker until the FE flag is on,
        // so custom discounts stay consistent with the (flag-gated) RTCE input tab.
        // TODO::Remove this flag-gated filter once RTCE is stabilized on production.
        if (!IS_RTCE_LIVE_IN_CE1) {
          metricsListToUse = metricsListToUse.filter(
            (x) => !RTCE_MLD_METRIC_NAMES.includes(x.name)
          );
        }

        // Native Inference CCU (compute-based) pricing is flag-gated on the input tab, so keep
        // its MLD metric out of the picker until CCU is GA.
        if (!IS_NATIVE_INFERENCE_CCU_LIVE_IN_CE1) {
          metricsListToUse = metricsListToUse.filter(
            (x) => x.name !== NATIVE_INFERENCE_CCU_MLD_METRIC_NAME
          );
        }

        // CC Gateway Add On is flag-gated (and unsupported on FedRAMP) on the Estimate Commit
        // Details form, so keep its (dimension-less) MLD metrics out of the picker under the
        // same conditions.
        if (!IS_GATEWAY_ADD_ON_LIVE_IN_CE1 || isFedRAMPEstimate) {
          metricsListToUse = metricsListToUse.filter(
            (x) => !GATEWAY_ADD_ON_MLD_METRIC_NAMES.includes(x.name)
          );
        }

        if (!isUserAdmin) {
          // For Admin Users, there is no restriction on metrics but for Field ( NA users ), we need to disable some metrics , hence, calling this function
          metricsListToUse = getMetricsByAddingDisabledForFieldBasedOnGuidanceAndUsage(
            metricsListToUse,
            mldGuidance
          );
        }

        return (
          <>
            <FieldArrayGridSelectField
              columnConfig={metricNameConfig}
              disableWhenSet={true}
              options={getMetricNameOptions(metricsListToUse, rowValues, allRows)}
            />
          </>
        );
      }}
    </DataFetcherContainer>
  );
};
